import { FileUpload } from "@admin-ui/components/application/file-upload/file-upload-base";
import { SectionFooter } from "@admin-ui/components/application/section-footers/section-footer";
import { SectionHeader } from "@admin-ui/components/application/section-headers/section-headers";
import { SectionLabel } from "@admin-ui/components/application/section-headers/section-label";
import { InputDate } from "@admin-ui/components/base/input/input-date";
import { NativeSelect } from "@admin-ui/components/base/select/select-native";
import { LifecycleBadge } from "@admin-ui/components/composed/LifecycleBadge";
import { TableEmptyState } from "@admin-ui/components/composed/table-empty-state";
import { ConfirmationDialog } from "@admin-ui/components/primitives/application/confirmation-dialog";
import { Table, TableCard } from "@admin-ui/components/primitives/application/table/table";
import { Avatar } from "@admin-ui/components/primitives/base/avatar/avatar";
import { Badge, BadgeWithDot } from "@admin-ui/components/primitives/base/badges/badges";
import { Button } from "@admin-ui/components/primitives/base/buttons/button";
import { Dropdown } from "@admin-ui/components/primitives/base/dropdown/dropdown";
import { Input } from "@admin-ui/components/primitives/base/input/input";
import { RadioButton, RadioGroup } from "@admin-ui/components/primitives/base/radio-buttons/radio-buttons";
import { accountSetupStatusColors, accountTypeColors, formatSetupStatus } from "@admin-ui/utils/account-display";
import { formatDate } from "@admin-ui/utils/format-date";
import { parseDate } from "@internationalized/date";
import type { AdminAccountsAccountMember, AdminAccountsAccountShow } from "@travily/types/models";
import { Edit05, Trash01, UserPlus01, Users01 } from "@untitledui/icons";
import { useState } from "react";
import { Link, TableBody } from "react-aria-components";
import { firstError } from "@/lib/forms/first-error";

export interface ShowViewProps {
  account: AdminAccountsAccountShow;
  members: AdminAccountsAccountMember[];
  onRemoveMember: (membershipId: number) => void;
  onEditMember: (member: AdminAccountsAccountMember) => void;
  adminUserPath: (id: number) => string;
}

export function ShowView({ members, onRemoveMember, onEditMember, adminUserPath }: ShowViewProps) {
  const [pendingRemove, setPendingRemove] = useState<number | null>(null);
  const memberCount = `${members.length} ${members.length === 1 ? "member" : "members"}`;

  return (
    <>
      <TableCard.Root>
        <TableCard.Header title="Members" badge={memberCount} />

        {members.length === 0 ? (
          <TableEmptyState
            icon={Users01}
            title="No members yet"
            description="Add people to this account to get started."
          />
        ) : (
          <Table aria-label="Members">
            <Table.Header>
              <Table.Head id="name" label="Name" isRowHeader />
              <Table.Head id="email" label="Email" />
              <Table.Head id="dob" label="Date of Birth" />
              <Table.Head id="joined" label="Joined" />
              <Table.Head id="actions" label="" />
            </Table.Header>
            <TableBody items={members}>
              {(member: AdminAccountsAccountMember) => (
                <Table.Row key={member.id} id={member.id}>
                  <Table.Cell>
                    {member.has_user ? (
                      <Link
                        href={adminUserPath(member.user_id as number)}
                        className="text-sm font-medium text-brand-600 hover:text-brand-700"
                      >
                        {member.person_name}
                      </Link>
                    ) : (
                      <span className="text-sm font-medium text-primary">{member.person_name}</span>
                    )}
                  </Table.Cell>
                  <Table.Cell>
                    <span className="text-sm text-tertiary">{member.user_email ?? "-"}</span>
                  </Table.Cell>
                  <Table.Cell>
                    <span className="text-sm text-tertiary">{member.person_date_of_birth ?? "-"}</span>
                  </Table.Cell>
                  <Table.Cell>{formatDate(member.created_at)}</Table.Cell>
                  <Table.Cell>
                    <Dropdown.Root>
                      <Dropdown.DotsButton />
                      <Dropdown.Popover className="w-min">
                        <Dropdown.Menu
                          onAction={(key) => {
                            if (key === "edit") onEditMember(member);
                            if (key === "remove") setPendingRemove(member.id);
                          }}
                        >
                          <Dropdown.Item id="edit" icon={Edit05} label="Edit" />
                          <Dropdown.Item id="remove" icon={Trash01} label="Remove" />
                        </Dropdown.Menu>
                      </Dropdown.Popover>
                    </Dropdown.Root>
                  </Table.Cell>
                </Table.Row>
              )}
            </TableBody>
          </Table>
        )}
      </TableCard.Root>

      {pendingRemove != null && (
        <ConfirmationDialog
          title="Remove member"
          description="This will remove the person from this account. They will lose access to any shared content."
          confirmLabel="Remove"
          color="error"
          isOpen
          onOpenChange={(open) => {
            if (!open) setPendingRemove(null);
          }}
          onConfirm={() => {
            onRemoveMember(pendingRemove);
            setPendingRemove(null);
          }}
        />
      )}
    </>
  );
}

type Visibility = "public" | "private" | "hidden";

export interface ProfileFormData {
  name: string;
  handle: string;
  visibility: Visibility;
  featured_on: string;
  feature_url: string;
}

export interface ProfileFormProps {
  account: AdminAccountsAccountShow;
  formData: ProfileFormData;
  errors: Partial<Record<string, string | string[]>>;
  avatarPreview: string | null;
  coverImagePreview: string | null;
  processing: boolean;
  onNameChange: (value: string) => void;
  onHandleChange: (value: string) => void;
  onVisibilityChange: (value: Visibility) => void;
  onFeaturedOnChange: (value: string) => void;
  onFeatureUrlChange: (value: string) => void;
  onAvatarDrop: (files: FileList) => void;
  onAvatarRemove: () => void;
  onCoverImageDrop: (files: FileList) => void;
  onCoverImageRemove: () => void;
  onSubmit: (e: React.FormEvent) => void;
}

function errorOf(errors: Partial<Record<string, string | string[]>>, field: string) {
  return firstError(errors[field]);
}

export function ProfileForm({
  account,
  formData,
  errors,
  avatarPreview,
  coverImagePreview,
  processing,
  onNameChange,
  onHandleChange,
  onVisibilityChange,
  onFeaturedOnChange,
  onFeatureUrlChange,
  onAvatarDrop,
  onAvatarRemove,
  onCoverImageDrop,
  onCoverImageRemove,
  onSubmit,
}: ProfileFormProps) {
  const isNameEditable = account.profile_type !== "Individual";
  // The Squiggle features families and communities; those are also the only
  // kinds with a public surface the highlight can render on.
  const isFeaturable = account.profile_type === "Family" || account.profile_type === "Community";

  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-6 pt-8">
      <SectionHeader.Root>
        <SectionHeader.Group>
          <div className="flex flex-1 flex-col justify-center gap-0.5 self-stretch">
            <SectionHeader.Heading>Profile</SectionHeader.Heading>
            <SectionHeader.Subheading>Identity, presentation, and visibility.</SectionHeader.Subheading>
          </div>
          <SectionHeader.Actions>
            <Button type="submit" color="primary" size="sm" isDisabled={processing}>
              Save
            </Button>
          </SectionHeader.Actions>
        </SectionHeader.Group>
      </SectionHeader.Root>

      <div className="flex flex-col gap-5">
        {isNameEditable && (
          <>
            <div className="grid grid-cols-1 lg:grid-cols-[minmax(200px,280px)_minmax(400px,512px)] lg:gap-8">
              <SectionLabel.Root size="sm" title="Display Name" className="max-lg:hidden" />
              <Input
                label="Display Name"
                value={formData.name}
                onChange={onNameChange}
                isInvalid={!!errors.name}
                hint={errorOf(errors, "name")}
                className="lg:label:hidden"
              />
            </div>
            <hr className="h-px w-full border-none bg-border-secondary" />
          </>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-[minmax(200px,280px)_minmax(400px,512px)] lg:gap-8">
          <SectionLabel.Root size="sm" title="Handle" className="max-lg:hidden" />
          <Input
            label="Handle"
            value={formData.handle}
            onChange={onHandleChange}
            isInvalid={!!errors.handle}
            hint={errorOf(errors, "handle")}
            className="lg:label:hidden"
          />
        </div>

        <hr className="h-px w-full border-none bg-border-secondary" />

        <div className="grid grid-cols-1 gap-5 lg:grid-cols-[minmax(200px,280px)_minmax(400px,512px)] lg:gap-8">
          <SectionLabel.Root size="sm" title="Profile photo" description="Square image, max 10 MB." />
          {avatarPreview ? (
            <div className="flex items-center gap-4">
              <Avatar size="xl" src={avatarPreview} />
              <Button size="sm" color="secondary" type="button" onClick={onAvatarRemove}>
                Remove
              </Button>
            </div>
          ) : (
            <FileUpload.Root>
              <FileUpload.DropZone
                accept="image/jpeg,image/png,image/webp"
                maxSize={10 * 1024 * 1024}
                onDropFiles={onAvatarDrop}
              />
            </FileUpload.Root>
          )}
        </div>

        <hr className="h-px w-full border-none bg-border-secondary" />

        <div className="grid grid-cols-1 gap-5 lg:grid-cols-[minmax(200px,280px)_minmax(400px,512px)] lg:gap-8">
          <SectionLabel.Root size="sm" title="Cover image" description="Wide image, max 10 MB." />
          {coverImagePreview ? (
            <div className="relative">
              <img
                src={coverImagePreview}
                alt="Cover preview"
                className="aspect-[3/1] w-full rounded-lg object-cover"
              />
              <Button
                size="sm"
                color="secondary"
                type="button"
                className="absolute right-2 bottom-2"
                onClick={onCoverImageRemove}
              >
                Remove
              </Button>
            </div>
          ) : (
            <FileUpload.Root>
              <FileUpload.DropZone
                accept="image/jpeg,image/png,image/webp"
                maxSize={10 * 1024 * 1024}
                onDropFiles={onCoverImageDrop}
              />
            </FileUpload.Root>
          )}
        </div>

        <hr className="h-px w-full border-none bg-border-secondary" />

        <div className="grid grid-cols-1 gap-5 lg:grid-cols-[minmax(200px,280px)_minmax(400px,512px)] lg:gap-8">
          <SectionLabel.Root
            size="sm"
            title="Visibility"
            description="Controls who can see this account's profile and content."
          />
          <RadioGroup
            aria-label="Visibility"
            value={formData.visibility}
            onChange={(next) => onVisibilityChange(next as Visibility)}
            className="gap-2"
          >
            <RadioButton value="public" label="Public" hint="Show at /@handle with a claim banner." />
            <RadioButton value="private" label="Private" hint="Profile visible but content hidden from non-friends." />
            <RadioButton value="hidden" label="Hidden" hint="Only visible to admin." />
          </RadioGroup>
        </div>

        {isFeaturable && (
          <>
            <hr className="h-px w-full border-none bg-border-secondary" />

            <div className="grid grid-cols-1 gap-5 lg:grid-cols-[minmax(200px,280px)_minmax(400px,512px)] lg:gap-8">
              <SectionLabel.Root
                size="sm"
                title="Feature"
                description="The Squiggle feature. Set both fields, or neither, to clear it."
              />
              <div className="flex flex-col gap-4">
                <InputDate
                  label="Featured on"
                  value={formData.featured_on ? parseDate(formData.featured_on) : null}
                  onChange={(value) => onFeaturedOnChange(value?.toString() ?? "")}
                  isInvalid={!!errors.featured_on}
                  hint={errorOf(errors, "featured_on")}
                />
                <Input
                  label="Feature URL"
                  placeholder="https://livetravily.com/squiggle/…"
                  value={formData.feature_url}
                  onChange={onFeatureUrlChange}
                  isInvalid={!!errors.feature_url}
                  hint={errorOf(errors, "feature_url")}
                />
              </div>
            </div>
          </>
        )}
      </div>

      <SectionFooter.Root>
        <SectionFooter.Actions>
          <Button type="submit" color="primary" size="sm" isDisabled={processing}>
            Save
          </Button>
        </SectionFooter.Actions>
      </SectionFooter.Root>
    </form>
  );
}

export interface SellingFormData {
  country: string;
}

export interface SellingFormProps {
  formData: SellingFormData;
  currency: string | null;
  errors: Partial<Record<string, string | string[]>>;
  processing: boolean;
  supportedCountries: Array<{ code: string; name: string }>;
  onCountryChange: (value: string) => void;
  onSubmit: (e: React.FormEvent) => void;
}

export function SellingForm({
  formData,
  currency,
  errors,
  processing,
  supportedCountries,
  onCountryChange,
  onSubmit,
}: SellingFormProps) {
  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-6 pt-8">
      <SectionHeader.Root>
        <SectionHeader.Group>
          <div className="flex flex-1 flex-col justify-center gap-0.5 self-stretch">
            <SectionHeader.Heading>Selling</SectionHeader.Heading>
            <SectionHeader.Subheading>
              Required only for accounts that sell products or services. Country drives Stripe Connect payouts.
            </SectionHeader.Subheading>
          </div>
          <SectionHeader.Actions>
            <Button type="submit" color="primary" size="sm" isDisabled={processing}>
              Save
            </Button>
          </SectionHeader.Actions>
        </SectionHeader.Group>
      </SectionHeader.Root>

      <div className="flex flex-col gap-5">
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(200px,280px)_minmax(400px,512px)] lg:gap-8">
          <SectionLabel.Root
            size="sm"
            title="Selling country"
            description="Where this account sells from. It can be set but not cleared, and Stripe locks it once payouts connect."
            className="max-lg:hidden"
          />
          <NativeSelect
            label="Selling country"
            value={formData.country}
            onChange={(e) => onCountryChange(e.target.value)}
            hint={errorOf(errors, "country")}
            options={[
              { label: "None", value: "" },
              ...supportedCountries.map((c) => ({ label: c.name, value: c.code })),
            ]}
            size="sm"
            className="lg:label:hidden"
          />
        </div>

        <hr className="h-px w-full border-none bg-border-secondary" />

        <div className="grid grid-cols-1 lg:grid-cols-[minmax(200px,280px)_minmax(400px,512px)] lg:gap-8">
          <SectionLabel.Root
            size="sm"
            title="Currency"
            description="Derived from the selling country — it is not set directly."
          />
          <p className="text-sm text-primary max-lg:pt-1.5 lg:self-center">{currency ?? "—"}</p>
        </div>
      </div>

      <SectionFooter.Root>
        <SectionFooter.Actions>
          <Button type="submit" color="primary" size="sm" isDisabled={processing}>
            Save
          </Button>
        </SectionFooter.Actions>
      </SectionFooter.Root>
    </form>
  );
}

export interface AccountMetaProps {
  account: AdminAccountsAccountShow;
}

export function AccountMeta({ account }: AccountMetaProps) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <Badge color={accountTypeColors[account.profile_type as keyof typeof accountTypeColors] ?? "gray"} size="sm">
        {account.profile_type?.toLowerCase() ?? "unknown"}
      </Badge>
      <BadgeWithDot color={accountSetupStatusColors[account.setup_status]} size="sm" type="pill-color">
        {formatSetupStatus(account.setup_status)}
      </BadgeWithDot>
      {account.lifecycle_state === "anonymized" && (
        <LifecycleBadge lifecycleState={account.lifecycle_state} anonymizedAt={account.anonymized_at} />
      )}
      <span className="text-xs text-tertiary">Created {formatDate(account.created_at)}</span>
    </div>
  );
}

interface MembersTabHeaderProps {
  memberCount: number;
  onAddMember: () => void;
}

export function MembersTabHeader({ memberCount, onAddMember }: MembersTabHeaderProps) {
  return (
    <SectionHeader.Root>
      <SectionHeader.Group>
        <div className="flex flex-1 flex-col justify-center gap-0.5 self-stretch">
          <SectionHeader.Heading>Members</SectionHeader.Heading>
          <SectionHeader.Subheading>
            {memberCount === 0
              ? "No members on this account yet."
              : `${memberCount} ${memberCount === 1 ? "person" : "people"} on this account.`}
          </SectionHeader.Subheading>
        </div>
        <SectionHeader.Actions>
          <Button color="primary" size="sm" iconLeading={UserPlus01} onClick={onAddMember}>
            Add member
          </Button>
        </SectionHeader.Actions>
      </SectionHeader.Group>
    </SectionHeader.Root>
  );
}
